/**
 * Timezone-aware midnight and day boundary utilities
 *
 * These utilities handle midnight calculations in a specific timezone,
 * which is essential for hospital scheduling where times should be
 * relative to the hospital's timezone, not the user's browser timezone.
 */
import dayjs, { type Dayjs } from './dayjs-config';
import { toDayjs } from './parse-iso-safe';

/**
 * Get midnight (start of day) in a specific timezone for a given date
 *
 * This is the timezone-aware equivalent of dayjs(date).startOf('day').
 * It returns midnight as it would be in the specified timezone.
 *
 * @param date - The date to get midnight for
 * @param timezone - The timezone to calculate midnight in (e.g., 'America/New_York')
 * @returns dayjs instance representing midnight in the specified timezone, or null if invalid
 *
 * @example
 * // Get midnight in Tokyo for a UTC date
 * const midnight = getMidnightInTimezone('2024-01-15T10:00:00Z', 'Asia/Tokyo');
 * // Returns: 2024-01-15T00:00:00+09:00 (midnight in Tokyo)
 */
export function getMidnightInTimezone(date: Date | Dayjs | string | null | undefined, timezone: string): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  try {
    // Convert to the target timezone, then get start of day in that timezone
    return dayjsDate.tz(timezone).startOf('day');
  } catch {
    // Invalid timezone, fallback to UTC
    return dayjsDate.utc().startOf('day');
  }
}

/**
 * Get minutes elapsed since midnight in a specific timezone
 *
 * Useful for positioning elements on a timeline where the reference point
 * is midnight in the hospital's timezone.
 *
 * @param timestamp - The timestamp to calculate minutes from
 * @param date - The date to use for determining which day's midnight (optional, uses timestamp's date if not provided)
 * @param timezone - The timezone to calculate midnight in
 * @returns Number of minutes since midnight, or 0 if invalid inputs
 *
 * @example
 * // If it's 2:30 PM in New York
 * getMinutesSinceMidnightInTimezone('2024-01-15T19:30:00Z', null, 'America/New_York');
 * // Returns: 870 (14 * 60 + 30 = 14:30 = 870 minutes)
 */
export function getMinutesSinceMidnightInTimezone(
  timestamp: Date | Dayjs | string | null | undefined,
  date: Date | Dayjs | string | null | undefined,
  timezone: string,
): number {
  const dayjsTimestamp = toDayjs(timestamp);
  if (!dayjsTimestamp) return 0;

  // Use the provided date for midnight reference, or fall back to the timestamp's date
  const referenceDate = date ? toDayjs(date) : dayjsTimestamp;
  const midnight = getMidnightInTimezone(referenceDate, timezone);

  if (!midnight) return 0;

  // Convert timestamp to the same timezone for accurate comparison
  const timestampInTimezone = dayjsTimestamp.tz(timezone);

  return timestampInTimezone.diff(midnight, 'minute');
}

/**
 * Get the start and end of a day as ISO strings, in a specific timezone
 *
 * Useful for creating query parameters that fetch data for a specific day
 * in the hospital's timezone.
 *
 * @param date - The date to get bounds for
 * @param timezone - The timezone to calculate day bounds in
 * @returns Object with start and end ISO strings, or null values if invalid
 *
 * @example
 * // Get day bounds for January 15th in Tokyo
 * getDayBoundsInTimezone('2024-01-15', 'Asia/Tokyo');
 * // Returns: {
 * //   start: '2024-01-14T15:00:00.000Z', // Midnight Tokyo = 15:00 UTC previous day
 * //   end: '2024-01-15T15:00:00.000Z'    // Midnight next day Tokyo = 15:00 UTC
 * // }
 */
export function getDayBoundsInTimezone(
  date: Date | Dayjs | string | null | undefined,
  timezone: string,
): { start: string | null; end: string | null } {
  const midnight = getMidnightInTimezone(date, timezone);

  if (!midnight) {
    return { start: null, end: null };
  }

  const nextMidnight = midnight.add(1, 'day');

  return {
    start: midnight.toISOString(),
    end: nextMidnight.toISOString(),
  };
}

/**
 * Check if two dates are on the same day in a specific timezone
 *
 * This is the timezone-aware equivalent of isSameDay().
 * Two UTC timestamps might be on different days in one timezone
 * but the same day in another.
 *
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @param timezone - The timezone to use for day comparison
 * @returns True if both dates are on the same day in the specified timezone
 *
 * @example
 * // Check if two UTC times are the same day in Tokyo
 * isSameDayInTimezone('2024-01-15T23:00:00Z', '2024-01-16T01:00:00Z', 'Asia/Tokyo');
 * // Returns: true (both are January 16th in Tokyo: 08:00 and 10:00)
 *
 * isSameDayInTimezone('2024-01-15T23:00:00Z', '2024-01-16T01:00:00Z', 'America/New_York');
 * // Returns: false (Jan 15th 18:00 and Jan 15th 20:00 in NY... wait, both are Jan 15th!)
 */
export function isSameDayInTimezone(
  date1: Date | Dayjs | string | null | undefined,
  date2: Date | Dayjs | string | null | undefined,
  timezone: string,
): boolean {
  const dayjs1 = toDayjs(date1);
  const dayjs2 = toDayjs(date2);

  if (!dayjs1 || !dayjs2) return false;

  try {
    const date1InTimezone = dayjs1.tz(timezone);
    const date2InTimezone = dayjs2.tz(timezone);

    return date1InTimezone.isSame(date2InTimezone, 'day');
  } catch {
    // Invalid timezone, fallback to UTC comparison
    return dayjs1.utc().isSame(dayjs2.utc(), 'day');
  }
}
